import { useState, useEffect, FormEvent, useMemo } from 'react';
import { client } from '../../sdkAdapter';
import { PostApiCatalogProductCreateRequestProductTypeEnum } from '@shoplattice/api-sdk';
import MediaUploader from '../common/MediaUploader';
import MediaPickerModal from '../common/MediaPickerModal';
import DatePicker from '../form/date-picker';
import CatalogModalFrame from './CatalogModalFrame';
import Pagination from '../ui/pagination';
import {
    BrandRecord,
    CatalogImageRecord,
    CategoryRecord,
    CatalogPagination,
    ProductRecord,
    extractApiErrorMessage,
    extractBrandCollection,
    extractCategoryTree,
    flattenCategoryTree,
    extractProductCollection,
    extractProductDetail,
    extractProductList,
    sanitizeSlug,
    toBoolean,
} from './catalogApi';
import {
    confirmAction,
    showErrorAlert,
    showSuccessToast,
    showValidationAlert,
} from './catalogAlerts';
import { useSeoOptions } from '../../hooks/useSeoOptions';

interface ProductFilters {
    name: string;
    sku: string;
    brand: string;
    category: string;
    active: string;
    price_from: string;
    price_to: string;
}

interface ProductFormState {
    name: string;
    sku: string;
    product_type: PostApiCatalogProductCreateRequestProductTypeEnum;
    brand_id: string;
    categories_id: number[];
    store_id: number[];
    tags_id: number[];
    slug: string;
    price: number;
    quantity: number;
    active: boolean;
    description: string;
    short_description: string;
    main_description: string;
    related_products: ProductRecord[];
    recommended_products: ProductRecord[];
    model_no: string;
    item_weight: number | '';
    length: string;
    height: string;
    width: string;
    offer: string;
    offer_price: number | '';
    credit_price: number | '';
    offer_desc: string;
    from_date: string;
    to_date: string;
    special_price: number | '';
    special_price_start_date: string;
    special_price_end_date: string;
    voucher_code: string;
    voucher_validity: string;
    voucher_price: number | '';
    trending: boolean;
    rate: boolean;
    new: boolean;
    video_url: string;
    video_image_url: string;
    youtube_code: string;
    youtube_url: string;
    '360_view': string;
    seo_id: string;
    images: number[];
}

const emptyFilters = (): ProductFilters => ({
    name: '',
    sku: '',
    brand: '',
    category: '',
    active: '',
    price_from: '',
    price_to: '',
});

const emptyProductForm = (): ProductFormState => ({
    name: '',
    sku: '',
    product_type: PostApiCatalogProductCreateRequestProductTypeEnum.Simple,
    brand_id: '',
    categories_id: [],
    store_id: [],
    tags_id: [],
    slug: '',
    price: 0,
    quantity: 0,
    active: true,
    description: '',
    short_description: '',
    main_description: '',
    related_products: [],
    recommended_products: [],
    model_no: '',
    item_weight: '',
    length: '',
    height: '',
    width: '',
    offer: '',
    offer_price: '',
    credit_price: '',
    offer_desc: '',
    from_date: '',
    to_date: '',
    special_price: '',
    special_price_start_date: '',
    special_price_end_date: '',
    voucher_code: '',
    voucher_validity: '',
    voucher_price: '',
    trending: false,
    rate: false,
    new: false,
    video_url: '',
    video_image_url: '',
    youtube_code: '',
    youtube_url: '',
    '360_view': '',
    seo_id: '',
    images: [],
});

const isValidUrl = (value: string): boolean => {
    try {
        new URL(value);
        return true;
    } catch {
        return false;
    }
};

const toInputDate = (value: string | null | undefined): string => {
    if (!value) {
        return '';
    }

    const match = value.match(/^(\d{4}-\d{2}-\d{2})/);
    return match ? match[1] : value;
};

const parsePositiveInteger = (value: string): number | undefined => {
    if (!value.trim()) {
        return undefined;
    }

    const parsed = Number(value);
    if (!Number.isInteger(parsed) || parsed <= 0) {
        return undefined;
    }

    return parsed;
};

const normalizeOptionalNumber = (value: number | ''): number | undefined => {
    if (value === '' || !Number.isFinite(value) || value < 0) {
        return undefined;
    }

    return value;
};

const getFullUrl = (url?: string | null): string => {
    if (!url) {
        return '';
    }

    if (url.startsWith('http://') || url.startsWith('https://')) {
        return encodeURI(url);
    }

    const baseUrl = (import.meta.env.VITE_API_BASE || 'http://127.0.0.1:8000').replace(/\/$/, '');
    const cleanUrl = url.replace(/^\//, '');

    return encodeURI(`${baseUrl}/${cleanUrl}`);
};

const getImageSource = (image?: CatalogImageRecord | null): string => {
    const path = image?.path || image?.thumb_r || image?.thumb_m || image?.thumb_s || '';
    return getFullUrl(path);
};

const getProductListingImage = (product: ProductRecord): string =>
    getImageSource(product.images?.[0] || null) || getImageSource(product.files || null);

const getInitials = (value?: string, fallback = 'NA'): string => {
    const trimmed = value?.trim();
    if (!trimmed) {
        return fallback;
    }

    const parts = trimmed
        .split(/\s+/)
        .map((part) => part.replace(/[^a-z0-9]/gi, ''))
        .filter(Boolean);

    if (parts.length === 0) {
        return fallback;
    }

    if (parts.length === 1) {
        return parts[0].slice(0, 2).toUpperCase();
    }

    return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
};

export default function CatalogProducts() {
    const [products, setProducts] = useState<ProductRecord[]>([]);
    const [viewMode, setViewMode] = useState<'list' | 'grid' | 'table'>('list');
    const [brands, setBrands] = useState<BrandRecord[]>([]);
    const [categories, setCategories] = useState<CategoryRecord[]>([]);
    const [stores, setStores] = useState<any[]>([]);
    const [tags, setTags] = useState<any[]>([]);
    const [allProducts, setAllProducts] = useState<ProductRecord[]>([]);
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [message, setMessage] = useState('');
    const [editingId, setEditingId] = useState<number | null>(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isMediaModalOpen, setIsMediaModalOpen] = useState(false);
    const [filters, setFilters] = useState<ProductFilters>(emptyFilters);
    const [form, setForm] = useState<ProductFormState>(emptyProductForm);
    const [pagination, setPagination] = useState<CatalogPagination | null>(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(20);

    // ... existing code ...
    const [debouncedFilters, setDebouncedFilters] = useState<ProductFilters>(filters);
    const [activeTab, setActiveTab] = useState<'general' | 'content' | 'pricing' | 'logistics' | 'media' | 'variants'>('general');
    const [attributes, setAttributes] = useState<any[]>([]);
    const [attributeValues, setAttributeValues] = useState<any[]>([]);
    const [productVariants, setProductVariants] = useState<any[]>([]);
    const [variantForm, setVariantForm] = useState({
        attribute_id: '',
        value_ids: [] as number[],
        price: 0,
        qty: 0
    });
    const [tagSearch, setTagSearch] = useState('');
    const { seoOptions, seoOptionsLoading } = useSeoOptions();

    const brandsById = useMemo(() => {
        const map = new Map<number, BrandRecord>();
        brands.forEach((brand) => {
            if (typeof brand.id === 'number') {
                map.set(brand.id, brand);
            }
        });
        return map;
    }, [brands]);

    const categoriesById = useMemo(() => {
        const map = new Map<number, CategoryRecord>();
        flattenCategoryTree(categories).forEach((category) => {
            if (typeof category.id === 'number') {
                map.set(category.id, category);
            }
        });
        return map;
    }, [categories]);

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedFilters(filters);
        }, 500);
        return () => clearTimeout(handler);
    }, [filters]);

    useEffect(() => {
        fetchProducts();
    }, [debouncedFilters, currentPage, rowsPerPage]);

    useEffect(() => {
        setCurrentPage(1);
    }, [debouncedFilters.name, debouncedFilters.sku, debouncedFilters.brand, debouncedFilters.category, debouncedFilters.active, debouncedFilters.price_from, debouncedFilters.price_to]);

    useEffect(() => {
        fetchBrands();
        fetchCategories();
        fetchStores();
        fetchTags();
        fetchAttributes();
        fetchAllProductsForRelations();
    }, []);

    const fetchBrands = async () => {
        try {
            const res = await client.getApiCatalogBrands({}, {
                params: { list: 1 }
            }) as any;
            setBrands(extractBrandCollection(res).items);
        } catch (err) { console.error(err); }
    };

    const fetchCategories = async () => {
        try {
            const res = await client.getApiCatalogCategories({}, {
                params: { list: 1 }
            }) as any;
            setCategories(extractCategoryTree(res));
        } catch (err) { console.error(err); }
    };

    const fetchStores = async () => {
        try {
            const res = await client.getApiCatalogStores({}, {
                params: { list: 1 }
            }) as any;
            const data = res.data?.data || res.data?.data?.data || res.data || [];
            setStores(Array.isArray(data) ? data : []);
        } catch (err) { console.error(err); }
    };

    const fetchTags = async () => {
        try {
            const res = await client.getApiCatalogTags({}, {
                params: { list: 1 }
            }) as any;
            const data = res.data?.data || res.data?.data?.data || res.data || [];
            setTags(Array.isArray(data) ? data : []);
        } catch (err) { console.error(err); }
    };

    const fetchAllProductsForRelations = async () => {
        try {
            const res = await client.getApiCatalogProducts({}, {
                params: { list: 1 }
            }) as any;
            setAllProducts(extractProductList(res));
        } catch (err) { console.error(err); }
    };

    const fetchProducts = async () => {
        setLoading(true);
        try {
            const params: any = {};
            if (debouncedFilters.name) params.name = debouncedFilters.name;
            if (debouncedFilters.sku) params.sku = debouncedFilters.sku;
            if (debouncedFilters.brand) params.brand = debouncedFilters.brand;
            if (debouncedFilters.category) params.category = debouncedFilters.category;
            if (debouncedFilters.active !== '') params.active = debouncedFilters.active;
            if (debouncedFilters.price_from !== '') params.price_from = debouncedFilters.price_from;
            if (debouncedFilters.price_to !== '') params.price_to = debouncedFilters.price_to;
            params.page = currentPage;
            params.per_page = rowsPerPage;

            const res = await client.getApiCatalogProducts({}, { params }) as any;
            const { items, pagination: nextPagination } = extractProductCollection(res);
            setProducts(items);
            setPagination(nextPagination);

            if (nextPagination?.currentPage && nextPagination.currentPage !== currentPage) {
                setCurrentPage(nextPagination.currentPage);
            }

            if (nextPagination?.perPage && nextPagination.perPage !== rowsPerPage) {
                setRowsPerPage(nextPagination.perPage);
            }
        } catch (err: any) {
            console.error('Failed to fetch products:', err);
            // Don't clear products on rate limit error to avoid flickering/empty state
            if (err?.response?.status !== 429) {
                setProducts([]);
                setPagination(null);
            }
        } finally {
            setLoading(false);
        }
    };

    const handleResetFilters = () => {
        const initialFilters = emptyFilters();
        setFilters(initialFilters);
        setDebouncedFilters(initialFilters);
        setCurrentPage(1);
    };

    const fetchAttributes = async () => {
        try {
            const res = await client.getApiCatalogAttributes() as any;
            const data = res.data?.data?.data || res.data?.data || res.data || [];
            setAttributes(Array.isArray(data) ? data : []);
        } catch (err) { console.error(err); }
    };

    const fetchProductVariants = async (productId: number) => {
        try {
            const res = await client.getApiCatalogProductsAttributes() as any;
            const data = Array.isArray(res?.data?.data)
                ? res.data.data
                : Array.isArray(res?.data?.data?.data)
                    ? res.data.data.data
                    : Array.isArray(res?.data)
                        ? res.data
                        : [];
            const variants = Array.isArray(data) ? data.filter((v: any) => v.product_id === productId) : [];
            setProductVariants(variants);
        } catch (err) {
            console.error('Failed to fetch variants:', err);
            setProductVariants([]);
        }
    };

    const handleCreateVariant = async () => {
        if (!editingId) {
            showValidationAlert('Save the product first before adding variants.');
            return;
        }

        if (form.product_type !== 'configurable') {
            showValidationAlert('Only configurable products can have variants.');
            return;
        }

        if (!variantForm.attribute_id || variantForm.value_ids.length === 0) {
            showValidationAlert('Please select an attribute and a value.');
            return;
        }

        if (!Number.isFinite(variantForm.price) || variantForm.price < 0) {
            showValidationAlert('Variant price must be a valid number greater than or equal to 0.');
            return;
        }

        if (!Number.isInteger(variantForm.qty) || variantForm.qty < 0) {
            showValidationAlert('Variant quantity must be a whole number greater than or equal to 0.');
            return;
        }

        try {
            setLoading(true);
            await client.postApiCatalogProductAttributesCreate({
                postApiCatalogProductAttributesCreateRequest: {
                    product_id: editingId,
                    attribute_values: variantForm.value_ids,
                    qty: variantForm.qty,
                    price: variantForm.price
                }
            });
            setMessage('Variant created successfully');
            showSuccessToast('Variant created successfully');
            setVariantForm({ attribute_id: '', value_ids: [], price: 0, qty: 0 });
            setAttributeValues([]);
            await fetchProductVariants(editingId);
        } catch (err: any) {
            const errorMessage = err?.response?.data?.messages || err?.response?.data?.message || err?.message || 'Failed to create variant';
            await showErrorAlert(Array.isArray(errorMessage) ? errorMessage.join(', ') : errorMessage);
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteVariant = async (variantId: number) => {
        const isConfirmed = await confirmAction({
            text: 'This variant will be permanently deleted.',
            confirmButtonText: 'Delete Variant'
        });

        if (!isConfirmed) return;
        try {
            await client.postApiCatalogProductAttributesDelete({
                postApiCatalogProductAttributesDeleteRequest: {
                    id: variantId,
                    product_id: editingId as number
                }
            });
            if (editingId) fetchProductVariants(editingId);
            showSuccessToast('Variant deleted successfully');
        } catch (err: any) {
            await showErrorAlert(err?.response?.data?.message || err?.message || 'Failed to delete variant');
        }
    };

    const handleEdit = async (product: ProductRecord) => {
        let nextProduct: ProductRecord = product;

        try {
            const detailResponse = await client.getApiCatalogProductId({ id: String(product.id) }) as any;
            const detailProduct = extractProductDetail(detailResponse);

            if (detailProduct) {
                nextProduct = {
                    ...product,
                    ...detailProduct,
                    brands: detailProduct.brands || product.brands,
                    stores: detailProduct.stores || product.stores,
                    tags: detailProduct.tags || product.tags,
                    categories: detailProduct.categories || product.categories,
                    related_products: detailProduct.related_products || detailProduct.relatedProducts || product.related_products || product.relatedProducts,
                    recommended_products: detailProduct.recommended_products || detailProduct.recommendedProducts || product.recommended_products || product.recommendedProducts,
                    images: detailProduct.images || product.images,
                    files: detailProduct.files || product.files,
                };
            }
        } catch (err) {
            console.error('Failed to fetch product details:', err);
        }

        setEditingId(nextProduct.id);
        setForm({
            name: nextProduct.name || '',
            sku: nextProduct.sku || '',
            product_type: (nextProduct.product_type as PostApiCatalogProductCreateRequestProductTypeEnum) || PostApiCatalogProductCreateRequestProductTypeEnum.Simple,
            brand_id: nextProduct.brand_id?.toString() || nextProduct.brands?.id?.toString() || '',
            categories_id: nextProduct.categories?.map((category) => category.id) || [],
            store_id: nextProduct.stores?.map((store) => store.id) || [],
            tags_id: nextProduct.tags?.map((tag) => tag.id) || [],
            slug: nextProduct.slug || '',
            price: Number(nextProduct.price || 0),
            quantity: Number(nextProduct.quantity ?? nextProduct.qty ?? 0),
            active: toBoolean(nextProduct.active ?? true),
            description: nextProduct.description || '',
            short_description: nextProduct.short_description || '',
            main_description: nextProduct.main_description || '',
            related_products: nextProduct.related_products || nextProduct.relatedProducts || [],
            recommended_products: nextProduct.recommended_products || nextProduct.recommendedProducts || [],
            model_no: nextProduct.model_no || '',
            item_weight: typeof nextProduct.item_weight === 'number' ? nextProduct.item_weight : '',
            length: nextProduct.length || '',
            height: nextProduct.height || '',
            width: nextProduct.width || '',
            offer: nextProduct.offer || '',
            offer_price: typeof nextProduct.offer_price === 'number' ? nextProduct.offer_price : '',
            credit_price: typeof nextProduct.credit_price === 'number' ? nextProduct.credit_price : '',
            offer_desc: nextProduct.offer_desc || '',
            from_date: toInputDate(nextProduct.from_date),
            to_date: toInputDate(nextProduct.to_date),
            special_price: typeof nextProduct.special_price === 'number' ? nextProduct.special_price : '',
            special_price_start_date: toInputDate(nextProduct.special_price_start_date),
            special_price_end_date: toInputDate(nextProduct.special_price_end_date),
            voucher_code: nextProduct.voucher_code || '',
            voucher_validity: toInputDate(nextProduct.voucher_validity),
            voucher_price: typeof nextProduct.voucher_price === 'number' ? nextProduct.voucher_price : '',
            trending: toBoolean(nextProduct.trending),
            rate: toBoolean(nextProduct.rate),
            new: toBoolean(nextProduct.new),
            video_url: nextProduct.video_url || '',
            video_image_url: nextProduct.video_image_url || '',
            youtube_code: nextProduct.youtube_code || '',
            youtube_url: nextProduct.youtube_url || '',
            '360_view': nextProduct['360_view'] || '',
            seo_id: nextProduct.seo_id ? String(nextProduct.seo_id) : '',
            images: nextProduct.images?.map((img) => img.id).filter((id): id is number => typeof id === 'number') || [],
        });
        setActiveTab('general');
        setIsModalOpen(true);
        setMessage('');
        // Fetch variants if configurable
        if (nextProduct.product_type === 'configurable') {
            fetchProductVariants(nextProduct.id);
        }
    };

    const handleCancelEdit = () => {
        setEditingId(null);
        setForm(emptyProductForm());
        setIsModalOpen(false);
        setMessage('');
    };

    const buildProductPayload = () => {
        const name = form.name.trim();
        const sku = form.sku.trim();
        const slug = sanitizeSlug(form.slug);
        const voucherCode = form.voucher_code.trim();
        const description = form.description.trim();
        const shortDescription = form.short_description.trim();
        const mainDescription = form.main_description.trim();
        const modelNo = form.model_no.trim();
        const offer = form.offer.trim();
        const offerDescription = form.offer_desc.trim();
        const length = form.length.trim();
        const height = form.height.trim();
        const width = form.width.trim();
        const videoUrl = form.video_url.trim();
        const videoImageUrl = form.video_image_url.trim();
        const youtubeCode = form.youtube_code.trim();
        const youtubeUrl = form.youtube_url.trim();
        const view360 = form['360_view'].trim();
        const seoId = parsePositiveInteger(form.seo_id);
        const specialPrice = normalizeOptionalNumber(form.special_price);
        const offerPrice = normalizeOptionalNumber(form.offer_price);
        const creditPrice = normalizeOptionalNumber(form.credit_price);
        const voucherPrice = normalizeOptionalNumber(form.voucher_price);
        const itemWeight = normalizeOptionalNumber(form.item_weight);
        const brandId = Number(form.brand_id);

        if (!name || !sku || !Number.isInteger(brandId) || brandId <= 0 || form.categories_id.length === 0) {
            return { valid: false as const, message: 'Please fill in all required fields (Name, SKU, Brand, Category).' };
        }

        if (!Number.isFinite(form.price) || form.price < 0) {
            return { valid: false as const, message: 'Price must be a valid number greater than or equal to 0.' };
        }

        if (!Number.isInteger(form.quantity) || form.quantity < 0) {
            return { valid: false as const, message: 'Quantity must be a whole number greater than or equal to 0.' };
        }

        if (form.slug.trim() && !slug) {
            return { valid: false as const, message: 'Slug can only contain letters, numbers, and hyphens.' };
        }

        if (voucherCode.length > 100) {
            return { valid: false as const, message: 'Voucher code must be 100 characters or fewer.' };
        }

        if (form.voucher_validity && Number.isNaN(Date.parse(form.voucher_validity))) {
            return { valid: false as const, message: 'Voucher validity must be a valid date.' };
        }

        if (videoUrl && !isValidUrl(videoUrl)) {
            return { valid: false as const, message: 'Video URL must be a valid URL.' };
        }

        if (youtubeUrl && !isValidUrl(youtubeUrl)) {
            return { valid: false as const, message: 'YouTube URL must be a valid URL.' };
        }

        if (
            form.special_price_start_date &&
            form.special_price_end_date &&
            form.special_price_start_date > form.special_price_end_date
        ) {
            return { valid: false as const, message: 'Special price start date must be before or equal to the end date.' };
        }

        if (
            form.from_date &&
            form.to_date &&
            form.from_date > form.to_date
        ) {
            return { valid: false as const, message: 'Offer start date must be before or equal to the end date.' };
        }

        const relatedProducts = Array.from(new Set(form.related_products.map((product) => product.id).filter((id): id is number => Number.isInteger(id))));
        const recommendedProducts = Array.from(new Set(form.recommended_products.map((product) => product.id).filter((id): id is number => Number.isInteger(id))));

        return {
            valid: true as const,
            data: {
                name,
                sku,
                product_type: form.product_type,
                brand_id: brandId,
                categories_id: Array.from(new Set(form.categories_id)),
                price: form.price,
                qty: form.quantity,
                quantity: form.quantity,
                active: form.active,
                store_id: Array.from(new Set(form.store_id)),
                tags_id: Array.from(new Set(form.tags_id)),
                related_products: relatedProducts,
                recommended_products: recommendedProducts,
                images: Array.from(new Set(form.images)),
                rate: form.rate,
                trending: form.trending,
                new: form.new,
                ...(slug ? { slug } : {}),
                ...(description ? { description } : {}),
                ...(shortDescription ? { short_description: shortDescription } : {}),
                ...(mainDescription ? { main_description: mainDescription } : {}),
                ...(modelNo ? { model_no: modelNo } : {}),
                ...(typeof itemWeight === 'number' ? { item_weight: itemWeight } : {}),
                ...(length ? { length } : {}),
                ...(height ? { height } : {}),
                ...(width ? { width } : {}),
                ...(offer ? { offer } : {}),
                ...(typeof offerPrice === 'number' ? { offer_price: offerPrice } : {}),
                ...(typeof creditPrice === 'number' ? { credit_price: creditPrice } : {}),
                ...(offerDescription ? { offer_desc: offerDescription } : {}),
                ...(form.from_date ? { from_date: form.from_date } : {}),
                ...(form.to_date ? { to_date: form.to_date } : {}),
                ...(voucherCode ? { voucher_code: voucherCode } : {}),
                ...(form.voucher_validity ? { voucher_validity: form.voucher_validity } : {}),
                ...(typeof voucherPrice === 'number' ? { voucher_price: voucherPrice } : {}),
                ...(typeof specialPrice === 'number' ? { special_price: specialPrice } : {}),
                ...(form.special_price_start_date ? { special_price_start_date: form.special_price_start_date } : {}),
                ...(form.special_price_end_date ? { special_price_end_date: form.special_price_end_date } : {}),
                ...(videoUrl ? { video_url: videoUrl } : {}),
                ...(videoImageUrl ? { video_image_url: videoImageUrl } : {}),
                ...(youtubeCode ? { youtube_code: youtubeCode } : {}),
                ...(youtubeUrl ? { youtube_url: youtubeUrl } : {}),
                ...(view360 ? { '360_view': view360 } : {}),
                ...(typeof seoId === 'number' ? { seo_id: seoId } : {}),
            }
        };
    };

    const createProduct = async (e: FormEvent) => {
        e.preventDefault();
        const payload = buildProductPayload();
        if (!payload.valid) {
            setMessage(payload.message);
            showValidationAlert(payload.message);
            return;
        }
        setSubmitting(true);
        setMessage('');
        try {
            await client.postApiCatalogProductCreate({
                postApiCatalogProductCreateRequest: payload.data
            });
            setIsModalOpen(false);
            setMessage('Product created successfully!');
            showSuccessToast('Product created successfully');
            handleCancelEdit();
            setCurrentPage(1);
            fetchProducts();
        } catch (err: any) {
            const errorMessage = extractApiErrorMessage(err, 'Failed to create product');
            setMessage(errorMessage);
            await showErrorAlert(errorMessage);
        } finally {
            setSubmitting(false);
        }
    };

    const updateProduct = async (e: FormEvent) => {
        e.preventDefault();
        if (editingId === null) {
            return;
        }

        const payload = buildProductPayload();
        if (!payload.valid) {
            setMessage(payload.message);
            showValidationAlert(payload.message);
            return;
        }
        setSubmitting(true);
        setMessage('');
        try {
            await client.postApiCatalogProductUpdate({
                postApiCatalogProductUpdateRequest: {
                    id: editingId,
                    ...payload.data,
                }
            });
            setIsModalOpen(false);
            setMessage('Product updated successfully!');
            showSuccessToast('Product updated successfully');
            handleCancelEdit();
            fetchProducts();
        } catch (err: any) {
            const errorMessage = extractApiErrorMessage(err, 'Failed to update product');
            setMessage(errorMessage);
            await showErrorAlert(errorMessage);
        } finally {
            setSubmitting(false);
        }
    };

    const handleSubmit = (e: FormEvent) => {
        if (editingId !== null) {
            updateProduct(e);
        } else {
            createProduct(e);
        }
    };

    const handleDeleteProduct = async (productId: number) => {
        const isConfirmed = await confirmAction({
            text: 'This product will be permanently deleted.',
            confirmButtonText: 'Delete Product'
        });

        if (!isConfirmed) return;

        try {
            await client.postApiCatalogProductDelete({
                postApiCatalogProductDeleteRequest: { id: productId }
            } as any);

            if (editingId === productId) {
                handleCancelEdit();
            }

            fetchProducts();
            showSuccessToast('Product deleted successfully');
        } catch (err: any) {
            console.error(err);
            await showErrorAlert(extractApiErrorMessage(err, 'Failed to delete product'));
        }
    };

    const resolveEntityId = (value: unknown): number | null => {
        const source = (typeof value === 'object' && value !== null && 'id' in value)
            ? (value as { id?: unknown; }).id
            : value;
        const parsed = Number(source);
        return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
    };

    const resolveProductBrand = (product: ProductRecord): BrandRecord | null => {
        const relationBrand = product.brands || null;
        const brandId = resolveEntityId(product.brand_id);
        const relationBrandId = resolveEntityId(relationBrand?.id);
        const fallbackBrand =
            (brandId ? brandsById.get(brandId) : undefined) ||
            (relationBrandId ? brandsById.get(relationBrandId) : undefined) ||
            null;

        if (relationBrand && fallbackBrand) {
            return {
                ...fallbackBrand,
                ...relationBrand,
                images: relationBrand.images || fallbackBrand.images,
            };
        }

        return relationBrand || fallbackBrand;
    };

    const resolveProductCategory = (product: ProductRecord): CategoryRecord | null => {
        const relationCategory = product.categories?.[0] || null;
        const relationCategoryId = resolveEntityId(relationCategory?.id);
        const categoryIds = (product as { categories_id?: unknown; }).categories_id;
        const firstCategoryId = Array.isArray(categoryIds)
            ? resolveEntityId(categoryIds[0])
            : null;
        const fallbackCategory =
            (relationCategoryId ? categoriesById.get(relationCategoryId) : undefined) ||
            (firstCategoryId ? categoriesById.get(firstCategoryId) : undefined) ||
            null;

        if (relationCategory && fallbackCategory) {
            return {
                ...fallbackCategory,
                ...relationCategory,
                files: relationCategory.files || fallbackCategory.files,
                images: (relationCategory.images && relationCategory.images.length > 0)
                    ? relationCategory.images
                    : fallbackCategory.images,
            };
        }

        return relationCategory || fallbackCategory;
    };

    const totalItems = pagination?.total ?? products.length;
    const totalPages = Math.max(1, pagination?.lastPage ?? 1);

    return (
        <div className="space-y-6">
            {/* Filter Bar */}
            <div className="rounded-xl border border-gray-200 bg-white p-6 dark:border-gray-800 dark:bg-gray-900">
                <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-800 dark:text-white">Filters</h3>
                    <div className="flex items-center gap-4">
                        <button
                            onClick={handleResetFilters}
                            className="text-sm font-medium text-brand-500 hover:text-brand-600 dark:text-brand-400 dark:hover:text-brand-300"
                        >
                            Reset All
                        </button>
                        <button
                            onClick={() => {
                                handleCancelEdit();
                                setIsModalOpen(true);
                            }}
                            className="bg-brand-500 hover:bg-brand-600 text-white px-4 py-2 rounded-lg text-sm font-bold shadow-sm transition-all flex items-center gap-2"
                        >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
                            Create Product
                        </button>
                    </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                    <div>
                        <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1 uppercase tracking-wider">Name</label>
                        <input
                            type="text"
                            placeholder="Search by name..."
                            value={filters.name}
                            onChange={e => setFilters({ ...filters, name: e.target.value })}
                            className="w-full rounded-lg border border-gray-300 dark:border-gray-700 bg-transparent px-3 py-2 text-sm text-gray-800 dark:text-white focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 outline-none transition-all"
                        />
                    </div>
                    <div>
                        <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1 uppercase tracking-wider">SKU</label>
                        <input
                            type="text"
                            placeholder="Search by SKU..."
                            value={filters.sku}
                            onChange={e => setFilters({ ...filters, sku: e.target.value })}
                            className="w-full rounded-lg border border-gray-300 dark:border-gray-700 bg-transparent px-3 py-2 text-sm text-gray-800 dark:text-white focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 outline-none transition-all"
                        />
                    </div>
                    <div>
                        <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1 uppercase tracking-wider">Brand</label>
                        <select
                            value={filters.brand}
                            onChange={e => setFilters({ ...filters, brand: e.target.value })}
                            className="w-full rounded-lg border border-gray-300 dark:border-gray-700 bg-transparent px-3 py-2 text-sm text-gray-800 dark:text-white focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 outline-none transition-all"
                        >
                            <option value="">All Brands</option>
                            {brands.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                        </select>
                    </div>
                    <div>
                        <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1 uppercase tracking-wider">Category</label>
                        <select
                            value={filters.category}
                            onChange={e => setFilters({ ...filters, category: e.target.value })}
                            className="w-full rounded-lg border border-gray-300 dark:border-gray-700 bg-transparent px-3 py-2 text-sm text-gray-800 dark:text-white focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 outline-none transition-all"
                        >
                            <option value="">All Categories</option>
                            {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                        </select>
                    </div>
                    <div>
                        <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1 uppercase tracking-wider">Status</label>
                        <select
                            value={filters.active}
                            onChange={e => setFilters({ ...filters, active: e.target.value })}
                            className="w-full rounded-lg border border-gray-300 dark:border-gray-700 bg-transparent px-3 py-2 text-sm text-gray-800 dark:text-white focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 outline-none transition-all"
                        >
                            <option value="">All Statuses</option>
                            <option value="1">Active</option>
                            <option value="0">Inactive</option>
                        </select>
                    </div>
                    <div className="sm:col-span-2 flex gap-4">
                        <div className="flex-1">
                            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1 uppercase tracking-wider">Price Min</label>
                            <input
                                type="number"
                                placeholder="Min"
                                value={filters.price_from}
                                onChange={e => setFilters({ ...filters, price_from: e.target.value })}
                                className="w-full rounded-lg border border-gray-300 dark:border-gray-700 bg-transparent px-3 py-2 text-sm text-gray-800 dark:text-white focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 outline-none transition-all"
                            />
                        </div>
                        <div className="flex-1">
                            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1 uppercase tracking-wider">Price Max</label>
                            <input
                                type="number"
                                placeholder="Max"
                                value={filters.price_to}
                                onChange={e => setFilters({ ...filters, price_to: e.target.value })}
                                className="w-full rounded-lg border border-gray-300 dark:border-gray-700 bg-transparent px-3 py-2 text-sm text-gray-800 dark:text-white focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 outline-none transition-all"
                            />
                        </div>
                    </div>
                </div>
            </div>

            <CatalogModalFrame
                isOpen={isModalOpen}
                onClose={handleCancelEdit}
                title={editingId !== null ? 'Edit Product' : 'Create Product'}
                className="max-w-6xl"
                bodyClassName="p-6"
                formId="catalog-product-form"
                submitLabel={submitting ? (editingId !== null ? 'Saving...' : 'Creating...') : (editingId !== null ? 'Update Product' : 'Create Product')}
                submitDisabled={submitting}
            >
                <div className="flex border-b border-gray-200 dark:border-gray-800 mb-6 overflow-x-auto">
                                <button
                                    className={`px-4 py-3 text-sm font-bold border-b-2 transition-colors ${activeTab === 'general' ? 'border-brand-500 text-brand-500' : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'}`}
                                    onClick={() => setActiveTab('general')}
                                >
                                    General
                                </button>
                                <button
                                    className={`px-4 py-3 text-sm font-bold border-b-2 transition-colors ${activeTab === 'content' ? 'border-brand-500 text-brand-500' : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'}`}
                                    onClick={() => setActiveTab('content')}
                                >
                                    Content
                                </button>
                                <button
                                    className={`px-4 py-3 text-sm font-bold border-b-2 transition-colors ${activeTab === 'pricing' ? 'border-brand-500 text-brand-500' : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'}`}
                                    onClick={() => setActiveTab('pricing')}
                                >
                                    Pricing & Offers
                                </button>
                                <button
                                    className={`px-4 py-3 text-sm font-bold border-b-2 transition-colors ${activeTab === 'logistics' ? 'border-brand-500 text-brand-500' : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'}`}
                                    onClick={() => setActiveTab('logistics')}
                                >
                                    Logistics
                                </button>
                                <button
                                    className={`px-4 py-3 text-sm font-bold border-b-2 transition-colors ${activeTab === 'media' ? 'border-brand-500 text-brand-500' : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'}`}
                                    onClick={() => setActiveTab('media')}
                                >
                                    Multimedia
                                </button>
                                {form.product_type === 'configurable' && (
                                    <button
                                        className={`px-4 py-3 text-sm font-bold border-b-2 transition-colors ${activeTab === 'variants' ? 'border-brand-500 text-brand-500' : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'}`}
                                        onClick={() => setActiveTab('variants')}
                                        disabled={editingId === null}
                                        title={editingId === null ? "Save product first to add variants" : ""}
                                    >
                                        Variants
                                    </button>
                                )}
                </div>

                <form id="catalog-product-form" onSubmit={handleSubmit} className={activeTab === 'general' ? "grid grid-cols-1 lg:grid-cols-3 gap-8" : "block"}>
                                {activeTab === 'media' && (
                                    <div className="bg-white dark:bg-gray-900 p-6 rounded-xl border border-gray-200 dark:border-gray-800">
                                        <h4 className="text-lg font-bold text-gray-800 dark:text-white mb-4">Product Images</h4>
                                        <div className="mb-6 bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg flex items-start gap-3">
                                            <svg className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                                            <p className="text-sm text-blue-700 dark:text-blue-300">
                                                Upload high-quality images for your product. You can upload multiple images. The first image will be used as the main thumbnail.
                                            </p>
                                        </div>
                                        <MediaUploader
                                            onUpload={(ids) => setForm(prev => ({ ...prev, images: ids }))}
                                            initialImages={form.images}
                                            multiple={true}
                                            label="Gallery"
                                            folder="products"
                                        />
                                        <div className="flex justify-between items-center bg-gray-50 dark:bg-gray-800 p-3 rounded-xl border border-gray-200 dark:border-gray-700 mt-4">
                                            <span className="text-sm text-gray-600 dark:text-gray-400 font-medium">Select from Media Library</span>
                                            <button
                                                type="button"
                                                onClick={() => setIsMediaModalOpen(true)}
                                                className="text-sm text-brand-500 hover:text-brand-600 font-bold transition-colors"
                                            >
                                                Open Library
                                            </button>
                                        </div>
                                    </div>
                                )}
                                {/* General Tab Content */}
                                {activeTab === 'general' && (
                                    <>
                                        {/* Main Content (Left Column) */}
                                        <div className="lg:col-span-2 space-y-6">
                                            <div className="bg-gray-50/50 dark:bg-gray-800/30 p-6 rounded-xl border border-gray-100 dark:border-gray-800">
                                                <h4 className="text-sm font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest mb-4">Basic Information</h4>
                                                <div className="space-y-4">
                                                    <div>
                                                        <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1.5">Product Name</label>
                                                        <input
                                                            value={form.name}
                                                            onChange={e => setForm({ ...form, name: e.target.value })}
                                                            className="w-full rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-950 px-4 py-2.5 text-gray-800 dark:text-white focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 outline-none transition-all"
                                                            placeholder="Enter product title..."
                                                            required
                                                        />
                                                    </div>
                                                    <div className="grid grid-cols-2 gap-4">
                                                        <div>
                                                            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1.5">SKU</label>
                                                            <input
                                                                value={form.sku}
                                                                onChange={e => setForm({ ...form, sku: e.target.value })}
                                                                className="w-full rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-950 px-4 py-2.5 text-gray-800 dark:text-white focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 outline-none transition-all"
                                                                placeholder="e.g. PRD-001"
                                                                required
                                                            />
                                                        </div>
                                                        <div>
                                                            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1.5">Type</label>
                                                            <select
                                                                value={form.product_type}
                                                                onChange={e => setForm({ ...form, product_type: e.target.value as any })}
                                                                className="w-full rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-950 px-4 py-2.5 text-gray-800 dark:text-white focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 outline-none transition-all"
                                                                required
            >
                                                                <option value="simple">Simple</option>
                                                                <option value="configurable">Configurable</option>
                                                            </select>
                                                        </div>
                                                    </div>
                                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                                        <div>
                                                            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1.5">Price</label>
                                                            <div className="relative">
                                                                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 font-mono">$</span>
                                                                <input
                                                                    type="number"
                                                                    min={0}
                                                                    value={form.price}
                                                                    onChange={e => setForm({ ...form, price: Number(e.target.value) })}
                                                                    className="w-full rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-950 pl-8 pr-4 py-2.5 text-gray-800 dark:text-white focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 outline-none transition-all font-mono"
                                                                    required
                                                                />
                                                            </div>
                                                        </div>
                                                        <div>
                                                            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1.5">Quantity</label>
                                                            <input
                                                                type="number"
                                                                min={0}
                                                                value={form.quantity}
                                                                onChange={e => setForm({ ...form, quantity: Number(e.target.value) })}
                                                                className="w-full rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-950 px-4 py-2.5 text-gray-800 dark:text-white focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 outline-none transition-all"
                                                                required
                                                            />
                                                        </div>
                                                        <div>
                                                            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1.5">Slug</label>
                                                            <input
                                                                value={form.slug}
                                                                onChange={e => setForm({ ...form, slug: e.target.value })}
                                                                className="w-full rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-950 px-4 py-2.5 text-gray-800 dark:text-white focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 outline-none transition-all"
                                                                placeholder="Auto-generated if left blank"
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="bg-gray-50/50 dark:bg-gray-800/30 p-6 rounded-xl border border-gray-100 dark:border-gray-800">
                                                <h4 className="text-sm font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest mb-4">Product Setup</h4>
                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                    <div>
                                                        <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1.5">Brand</label>
                                                        <select
                                                            value={form.brand_id}
                                                            onChange={e => setForm({ ...form, brand_id: e.target.value })}
                                                            className="w-full rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-950 px-4 py-2.5 text-gray-800 dark:text-white focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 outline-none transition-all"
                                                            required
                                                        >
                                                            <option value="">Select Brand</option>
                                                            {brands.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                                                        </select>
                                                    </div>
                                                    <div>
                                                        <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1.5">SEO</label>
                                                        <select
                                                            value={form.seo_id}
                                                            onChange={e => setForm({ ...form, seo_id: e.target.value })}
                                                            className="w-full rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-950 px-4 py-2.5 text-gray-800 dark:text-white focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 outline-none transition-all appearance-none cursor-pointer"
                                                        >
                                                            <option value="">
                                                                {seoOptionsLoading ? 'Loading SEO records...' : 'Select SEO record'}
                                                            </option>
                                                            {seoOptions.map((seo) => (
                                                                <option key={seo.id} value={String(seo.id)}>
                                                                    #{seo.id} - {seo.meta_title?.trim() || seo.url}
                                                                </option>
                                                            ))}
                                                        </select>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Product Relations (Related & Recommended) */}
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                                <div className="bg-gray-50/50 dark:bg-gray-800/30 p-6 rounded-xl border border-gray-100 dark:border-gray-800">
                                                    <div className="flex items-center justify-between mb-4">
                                                        <h4 className="text-sm font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest">Related Products</h4>
                                                    </div>
                                                    <div className="space-y-3">
                                                        <select
                                                            onChange={(e) => {
                                                                const p = allProducts.find(x => x.id === Number(e.target.value));
                                                                if (p && !form.related_products.find(x => x.id === p.id)) {
                                                                    setForm({ ...form, related_products: [...form.related_products, p] });
                                                                }
                                                            }}
                                                            className="w-full rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-950 px-3 py-2 text-xs text-gray-800 dark:text-white outline-none"
                                                        >
                                                            <option value="">Add Related Product...</option>
                                                            {allProducts.filter(p => p.id !== editingId).map(p => (
                                                                <option key={p.id} value={p.id}>{p.name} (${p.price})</option>
                                                            ))}
                                                        </select>
                                                        <div className="space-y-2 max-h-[200px] overflow-y-auto">
                                                            {form.related_products.length === 0 ? (
                                                                <div className="h-[60px] flex items-center justify-center border-2 border-dashed border-gray-200 dark:border-gray-700 rounded-lg text-xs text-gray-500 font-medium whitespace-nowrap overflow-hidden px-2">
                                                                    No related products selected
                                                                </div>
                                                            ) : (
                                                                form.related_products.map(p => (
                                                                    <div key={p.id} className="flex items-center justify-between p-2 rounded-lg bg-white dark:bg-gray-900 border border-gray-100 dark:border-gray-800 text-xs shadow-sm">
                                                                        <span className="font-medium truncate mr-2 text-gray-700 dark:text-gray-300">{p.name}</span>
                                                                        <button
                                                                            type="button"
                                                                            onClick={() => setForm({ ...form, related_products: form.related_products.filter(x => x.id !== p.id) })}
                                                                            className="text-red-500 hover:text-red-600 font-bold"
                                                                        >
                                                                            &times;
                                                                        </button>
                                                                    </div>
                                                                ))
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="bg-gray-50/50 dark:bg-gray-800/30 p-6 rounded-xl border border-gray-100 dark:border-gray-800">
                                                    <div className="flex items-center justify-between mb-4">
                                                        <h4 className="text-sm font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest">Recommended</h4>
                                                    </div>
                                                    <div className="space-y-3">
                                                        <select
                                                            onChange={(e) => {
                                                                const p = allProducts.find(x => x.id === Number(e.target.value));
                                                                if (p && !form.recommended_products.find(x => x.id === p.id)) {
                                                                    setForm({ ...form, recommended_products: [...form.recommended_products, p] });
                                                                }
                                                            }}
                                                            className="w-full rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-950 px-3 py-2 text-xs text-gray-800 dark:text-white outline-none"
                                                        >
                                                            <option value="">Add Recommended Product...</option>
                                                            {allProducts.filter(p => p.id !== editingId).map(p => (
                                                                <option key={p.id} value={p.id}>{p.name} (${p.price})</option>
                                                            ))}
                                                        </select>
                                                        <div className="space-y-2 max-h-[200px] overflow-y-auto">
                                                            {form.recommended_products.length === 0 ? (
                                                                <div className="h-[60px] flex items-center justify-center border-2 border-dashed border-gray-200 dark:border-gray-700 rounded-lg text-xs text-gray-500 font-medium whitespace-nowrap overflow-hidden px-2">
                                                                    No recommended products selected
                                                                </div>
                                                            ) : (
                                                                form.recommended_products.map(p => (
                                                                    <div key={p.id} className="flex items-center justify-between p-2 rounded-lg bg-white dark:bg-gray-900 border border-gray-100 dark:border-gray-800 text-xs shadow-sm">
                                                                        <span className="font-medium truncate mr-2 text-gray-700 dark:text-gray-300">{p.name}</span>
                                                                        <button
                                                                            type="button"
                                                                            onClick={() => setForm({ ...form, recommended_products: form.recommended_products.filter(x => x.id !== p.id) })}
                                                                            className="text-red-500 hover:text-red-600 font-bold"
                                                                        >
                                                                            &times;
                                                                        </button>
                                                                    </div>
                                                                ))
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Sidebar (Right Column) */}
                                        <div className="space-y-6">
                                            <div className="bg-white dark:bg-gray-900 p-6 rounded-xl border border-gray-200 dark:border-gray-800 shadow-sm sticky top-6">
                                                <h4 className="text-sm font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest mb-6 border-b border-gray-100 dark:border-gray-800 pb-2">Organization</h4>

                                                <div className="space-y-6">
                                                    <div>
                                                        <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Categories</label>
                                                        <div className="max-h-[150px] overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-lg p-3 space-y-2 bg-gray-50 dark:bg-gray-800">
                                                            {categories.map(c => (
                                                                <label key={c.id} className="flex items-center gap-2 group cursor-pointer">
                                                                    <input
                                                                        type="checkbox"
                                                                        checked={form.categories_id.includes(c.id)}
                                                                        onChange={e => {
                                                                            const newCats = e.target.checked
                                                                                ? [...form.categories_id, c.id]
                                                                                : form.categories_id.filter(id => id !== c.id);
                                                                            setForm({ ...form, categories_id: newCats });
                                                                        }}
                                                                        className="rounded border-gray-300 text-brand-500 focus:ring-brand-500 h-4 w-4"
                                                                    />
                                                                    <span className="text-sm text-gray-600 dark:text-gray-400 group-hover:text-brand-500 transition-colors font-medium">{c.name}</span>
                                                                </label>
                                                            ))}
                                                        </div>
                                                    </div>

                                                    <div>
                                                        <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Stores</label>
                                                        <div className="max-h-[150px] overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-lg p-3 space-y-2 bg-gray-50 dark:bg-gray-800">
                                                            {stores.map(s => (
                                                                <label key={s.id} className="flex items-center gap-2 group cursor-pointer">
                                                                    <input
                                                                        type="checkbox"
                                                                        checked={form.store_id.includes(s.id)}
                                                                        onChange={e => {
                                                                            const newIds = e.target.checked
                                                                                ? [...form.store_id, s.id]
                                                                                : form.store_id.filter(id => id !== s.id);
                                                                            setForm({ ...form, store_id: newIds });
                                                                        }}
                                                                        className="rounded border-gray-300 text-brand-500 focus:ring-brand-500 h-4 w-4"
                                                                    />
                                                                    <span className="text-sm text-gray-600 dark:text-gray-400 group-hover:text-brand-500 transition-colors font-medium">{s.name}</span>
                                                                </label>
                                                            ))}
                                                        </div>
                                                    </div>

                                                    <div>
                                                        <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Tags</label>
                                                        <div className="mb-2">
                                                            <input
                                                                type="text"
                                                                placeholder="Search tags..."
                                                                value={tagSearch}
                                                                onChange={e => setTagSearch(e.target.value)}
                                                                className="w-full rounded-lg border border-gray-300 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 px-3 py-1.5 text-xs text-gray-800 dark:text-white outline-none focus:ring-2 focus:ring-brand-500/20"
                                                                onClick={(e) => e.stopPropagation()}
                                                            />
                                                        </div>
                                                        <div className="max-h-[150px] overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-lg p-3 space-y-2 bg-gray-50 dark:bg-gray-800 scrollbar-thin scrollbar-thumb-gray-300 dark:scrollbar-thumb-gray-600">
                                                            {tags.filter(t => t.name.toLowerCase().includes(tagSearch.toLowerCase())).map(t => (
                                                                <label key={t.id} className="flex items-center gap-2 group cursor-pointer">
                                                                    <input
                                                                        type="checkbox"
                                                                        checked={form.tags_id.includes(t.id)}
                                                                        onChange={e => {
                                                                            const newIds = e.target.checked
                                                                                ? [...form.tags_id, t.id]
                                                                                : form.tags_id.filter(id => id !== t.id);
                                                                            setForm({ ...form, tags_id: newIds });
                                                                        }}
                                                                        className="rounded border-gray-300 text-brand-500 focus:ring-brand-500 h-4 w-4"
                                                                    />
                                                                    <span className="text-sm text-gray-600 dark:text-gray-400 group-hover:text-brand-500 transition-colors font-medium">{t.name}</span>
                                                                </label>
                                                            ))}
                                                            {tags.filter(t => t.name.toLowerCase().includes(tagSearch.toLowerCase())).length === 0 && (
                                                                <p className="text-xs text-center text-gray-500 py-2">No tags found</p>
                                                            )}
                                                        </div>
                                                    </div>

                                                    <div className="pt-4 border-t border-gray-100 dark:border-gray-800">
                                                        <div className="flex items-center justify-between">
                                                            <span className="text-sm font-bold text-gray-700 dark:text-gray-300">Active Status</span>
                                                            <button
                                                                type="button"
                                                                onClick={() => setForm({ ...form, active: !form.active })}
                                                                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ring-2 ring-offset-2 ring-transparent focus:ring-brand-500 ${form.active ? 'bg-brand-500' : 'bg-gray-300 dark:bg-gray-700'}`}
                                                            >
                                                                <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${form.active ? 'translate-x-6' : 'translate-x-1'}`} />
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>

                                            {message && (
                                                <div className={`p-4 rounded-xl text-sm font-semibold ${message.includes('success') ? 'bg-green-50 text-green-700 border border-green-200 dark:bg-green-900/20 dark:text-green-400 dark:border-green-900/50' : 'bg-red-50 text-red-700 border border-red-200 dark:bg-red-900/20 dark:text-red-400 dark:border-red-900/50'}`}>
                                                    <div className="flex items-center gap-2">
                                                        {message.includes('success') ? (
                                                            <svg className="w-5 h-5 shrink-0" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" /></svg>
                                                        ) : (
                                                            <svg className="w-5 h-5 shrink-0" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" /></svg>
                                                        )}
                                                        {message}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </>
                                )}

                                {/* Content Tab */}
                                {activeTab === 'content' && (
                                    <div className="space-y-6">
                                        <div className="bg-gray-50/50 dark:bg-gray-800/30 p-6 rounded-xl border border-gray-100 dark:border-gray-800">
                                            <h4 className="text-sm font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest mb-4">Product Content</h4>
                                            <div className="space-y-6">
                                                <div>
                                                    <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1.5">Short Description</label>
                                                    <textarea
                                                        value={form.short_description}
                                                        onChange={e => setForm({ ...form, short_description: e.target.value })}
                                                        className="w-full rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-950 px-4 py-2 text-gray-800 dark:text-white outline-none min-h-[100px]"
                                                        placeholder="Brief overview..."
                                                    />
                                                </div>
                                                <div>
                                                    <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1.5">Main Description</label>
                                                    <textarea
                                                        value={form.main_description}
                                                        onChange={e => setForm({ ...form, main_description: e.target.value })}
                                                        className="w-full rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-950 px-4 py-2 text-gray-800 dark:text-white outline-none min-h-[250px]"
                                                        placeholder="Detailed features, specifications, etc."
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Pricing & Offers Tab */}
                                {activeTab === 'pricing' && (
                                    <div className="space-y-6">
                                        <div className="bg-gray-50/50 dark:bg-gray-800/30 p-6 rounded-xl border border-gray-100 dark:border-gray-800">
                                            <h4 className="text-sm font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest mb-4">Offer Pricing</h4>
                                            <div className="space-y-4">
                                                <div>
                                                    <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1.5">Offer Label</label>
                                                    <input
                                                        value={form.offer}
                                                        onChange={e => setForm({ ...form, offer: e.target.value })}
                                                        className="w-full rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-950 px-4 py-2 text-gray-800 dark:text-white outline-none"
                                                        placeholder="e.g. Summer Offer"
                                                    />
                                                </div>
                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                    <div>
                                                        <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1.5">Offer Price</label>
                                                        <input
                                                            type="number"
                                                            value={form.offer_price}
                                                            onChange={e => setForm({ ...form, offer_price: e.target.value === '' ? '' : Number(e.target.value) })}
                                                            className="w-full rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-950 px-4 py-2 text-gray-800 dark:text-white outline-none"
                                                        />
                                                    </div>
                                                    <div>
                                                        <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1.5">Credit Price</label>
                                                        <input
                                                            type="number"
                                                            value={form.credit_price}
                                                            onChange={e => setForm({ ...form, credit_price: e.target.value === '' ? '' : Number(e.target.value) })}
                                                            className="w-full rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-950 px-4 py-2 text-gray-800 dark:text-white outline-none"
                                                        />
                                                    </div>
                                                </div>
                                                <div className="grid grid-cols-2 gap-4">
                                                    <div>
                                                        <DatePicker
                                                            id="catalog-product-offer-start-date"
                                                            label="Offer Start Date"
                                                            placeholder="Select start date"
                                                            defaultDate={form.from_date || undefined}
                                                            onChange={(_selectedDates, dateStr) =>
                                                                setForm({ ...form, from_date: dateStr })
                                                            }
                                                        />
                                                    </div>
                                                    <div>
                                                        <DatePicker
                                                            id="catalog-product-offer-end-date"
                                                            label="Offer End Date"
                                                            placeholder="Select end date"
                                                            defaultDate={form.to_date || undefined}
                                                            onChange={(_selectedDates, dateStr) =>
                                                                setForm({ ...form, to_date: dateStr })
                                                            }
                                                        />
                                                    </div>
                                                </div>
                                                <div>
                                                    <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1.5">Offer Description</label>
                                                    <textarea
                                                        value={form.offer_desc}
                                                        onChange={e => setForm({ ...form, offer_desc: e.target.value })}
                                                        className="w-full rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-950 px-4 py-2 text-gray-800 dark:text-white outline-none min-h-[100px]"
                                                        placeholder="Optional offer details..."
                                                    />
                                                </div>
                                            </div>
                                        </div>

                                        <div className="grid grid-cols-1  ">
                                             

                                            <div className="bg-gray-50/50 dark:bg-gray-800/30 p-6 rounded-xl border border-gray-100 dark:border-gray-800">
                                                <h4 className="text-sm font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest mb-4">Voucher Settings</h4>
                                                <div className="space-y-4">
                                                    <div>
                                                        <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1.5">Voucher Code</label>
                                                        <input
                                                            value={form.voucher_code}
                                                            onChange={e => setForm({ ...form, voucher_code: e.target.value })}
                                                            className="w-full rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-950 px-4 py-2 text-gray-800 dark:text-white outline-none"
                                                        />
                                                    </div>
                                                    <div className="grid grid-cols-2 gap-4">
                                                        <div>
                                                            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1.5">Voucher Price</label>
                                                            <input
                                                                type="number"
                                                                value={form.voucher_price}
                                                                onChange={e => setForm({ ...form, voucher_price: e.target.value === '' ? '' : Number(e.target.value) })}
                                                                className="w-full rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-950 px-4 py-2 text-gray-800 dark:text-white outline-none"
                                                            />
                                                        </div>
                                                        <div>
                                                            <DatePicker
                                                                id="catalog-product-voucher-validity-date"
                                                                label="Validity Date"
                                                                placeholder="Select validity date"
                                                                defaultDate={form.voucher_validity || undefined}
                                                                onChange={(_selectedDates, dateStr) =>
                                                                    setForm({ ...form, voucher_validity: dateStr })
                                                                }
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="bg-gray-50/50 dark:bg-gray-800/30 p-6 rounded-xl border border-gray-100 dark:border-gray-800">
                                            <h4 className="text-sm font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest mb-4">Marketing Badges</h4>
                                            <div className="flex gap-8">
                                                <label className="flex items-center gap-2 cursor-pointer">
                                                    <input type="checkbox" checked={form.trending} onChange={e => setForm({ ...form, trending: e.target.checked })} className="rounded bg-brand-500" />
                                                    <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">Trending</span>
                                                </label>
                                                <label className="flex items-center gap-2 cursor-pointer">
                                                    <input type="checkbox" checked={form.rate} onChange={e => setForm({ ...form, rate: e.target.checked })} className="rounded bg-brand-500" />
                                                    <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">Allow Rating</span>
                                                </label>
                                                <label className="flex items-center gap-2 cursor-pointer">
                                                    <input type="checkbox" checked={form.new} onChange={e => setForm({ ...form, new: e.target.checked })} className="rounded bg-brand-500" />
                                                    <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">New Product</span>
                                                </label>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Logistics Tab */}
                                {activeTab === 'logistics' && (
                                    <div className="space-y-6">
                                        <div className="bg-gray-50/50 dark:bg-gray-800/30 p-6 rounded-xl border border-gray-100 dark:border-gray-800">
                                            <h4 className="text-sm font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest mb-4">Shipping & Dimensions</h4>
                                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                                <div>
                                                    <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1.5">Model Number</label>
                                                    <input
                                                        value={form.model_no}
                                                        onChange={e => setForm({ ...form, model_no: e.target.value })}
                                                        className="w-full rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-950 px-4 py-2 text-gray-800 dark:text-white outline-none"
                                                        placeholder="e.g. MX-200"
                                                    />
                                                </div>
                                                <div>
                                                    <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1.5">Weight (kg)</label>
                                                    <input
                                                        type="number"
                                                        value={form.item_weight}
                                                        onChange={e => setForm({ ...form, item_weight: e.target.value === '' ? '' : Number(e.target.value) })}
                                                        className="w-full rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-950 px-4 py-2 text-gray-800 dark:text-white outline-none"
                                                    />
                                                </div>
                                                <div className="grid grid-cols-3 gap-2">
                                                    <div>
                                                        <label className="block text-xs font-semibold text-gray-500 mb-1">Length</label>
                                                        <input value={form.length} onChange={e => setForm({ ...form, length: e.target.value })} className="w-full rounded-lg border px-2 py-2 text-xs" />
                                                    </div>
                                                    <div>
                                                        <label className="block text-xs font-semibold text-gray-500 mb-1">Height</label>
                                                        <input value={form.height} onChange={e => setForm({ ...form, height: e.target.value })} className="w-full rounded-lg border px-2 py-2 text-xs" />
                                                    </div>
                                                    <div>
                                                        <label className="block text-xs font-semibold text-gray-500 mb-1">Width</label>
                                                        <input value={form.width} onChange={e => setForm({ ...form, width: e.target.value })} className="w-full rounded-lg border px-2 py-2 text-xs" />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Multimedia Tab */}
                                {activeTab === 'media' && (
                                    <div className="space-y-6">
                                        <div className="bg-gray-50/50 dark:bg-gray-800/30 p-6 rounded-xl border border-gray-100 dark:border-gray-800">
                                            <h4 className="text-sm font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest mb-4">Video & Views</h4>
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                                <div>
                                                    <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1.5">Video URL</label>
                                                    <input
                                                        value={form.video_url}
                                                        onChange={e => setForm({ ...form, video_url: e.target.value })}
                                                        className="w-full rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-950 px-4 py-2 text-gray-800 dark:text-white outline-none"
                                                        placeholder="https://..."
                                                    />
                                                </div>
                                                <div>
                                                    <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1.5">Video Image URL</label>
                                                    <input
                                                        value={form.video_image_url}
                                                        onChange={e => setForm({ ...form, video_image_url: e.target.value })}
                                                        className="w-full rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-950 px-4 py-2 text-gray-800 dark:text-white outline-none"
                                                        placeholder="https://..."
                                                    />
                                                </div>
                                                <div>
                                                    <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1.5">YouTube Code</label>
                                                    <input
                                                        value={form.youtube_code}
                                                        onChange={e => setForm({ ...form, youtube_code: e.target.value })}
                                                        className="w-full rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-950 px-4 py-2 text-gray-800 dark:text-white outline-none"
                                                        placeholder="e.g. dQw4w9WgXcQ"
                                                    />
                                                </div>
                                                <div>
                                                    <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1.5">YouTube URL</label>
                                                    <input
                                                        value={form.youtube_url}
                                                        onChange={e => setForm({ ...form, youtube_url: e.target.value })}
                                                        className="w-full rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-950 px-4 py-2 text-gray-800 dark:text-white outline-none"
                                                        placeholder="https://..."
                                                    />
                                                </div>
                                                <div className="md:col-span-2">
                                                    <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1.5">360 View URL</label>
                                                    <input
                                                        value={form['360_view']}
                                                        onChange={e => setForm({ ...form, '360_view': e.target.value })}
                                                        className="w-full rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-950 px-4 py-2 text-gray-800 dark:text-white outline-none"
                                                        placeholder="https://..."
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Variants Tab Content */}
                                {activeTab === 'variants' && (
                                    <div className="space-y-6">

                                        {/* Variant Creation Form */}
                                        <div className="bg-gray-50/50 dark:bg-gray-800/30 p-6 rounded-xl border border-gray-100 dark:border-gray-800">
                                            <h4 className="text-sm font-bold text-gray-700 dark:text-gray-300 uppercase tracking-widest mb-4">Add New Variant</h4>
                                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 items-end">
                                                <div>
                                                    <label className="block text-xs font-semibold text-gray-500 mb-1">Attribute</label>
                                                    <select
                                                        value={variantForm.attribute_id}
                                                        onChange={async (e) => {
                                                            const attrId = e.target.value;
                                                            setVariantForm({ ...variantForm, attribute_id: attrId, value_ids: [] });
                                                            if (attrId) {
                                                                try {
                                                                    const res = await client.getApiCatalogAttributesValues() as any;
                                                                    const allValues = Array.isArray(res?.data?.data)
                                                                        ? res.data.data
                                                                        : Array.isArray(res?.data?.data?.data)
                                                                            ? res.data.data.data
                                                                            : Array.isArray(res?.data)
                                                                                ? res.data
                                                                                : [];
                                                                    const filtered = Array.isArray(allValues)
                                                                        ? allValues.filter((v: any) => Number(v.attribute_id ?? v.attribute?.id) === Number(attrId))
                                                                        : [];
                                                                    setAttributeValues(filtered);
                                                                } catch (error) { console.error(error); setAttributeValues([]); }
                                                            } else {
                                                                setAttributeValues([]);
                                                            }
                                                        }}
                                                        className="w-full rounded-lg border border-gray-300 dark:border-gray-700 px-3 py-2 text-sm dark:bg-gray-900 dark:text-white"
                                                    >
                                                        <option value="">Select Attribute...</option>
                                                        {attributes.map(a => (
                                                            <option key={a.id} value={a.id}>
                                                                {a.name}{a.type ? ` (${a.type})` : ''}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </div>
                                                <div>
                                                    <label className="block text-xs font-semibold text-gray-500 mb-1">Value</label>
                                                    <select
                                                        value={variantForm.value_ids[0] || ''}
                                                        onChange={e => setVariantForm({ ...variantForm, value_ids: [Number(e.target.value)] })}
                                                        disabled={!variantForm.attribute_id}
                                                        className="w-full rounded-lg border border-gray-300 dark:border-gray-700 px-3 py-2 text-sm dark:bg-gray-900 dark:text-white disabled:opacity-50"
                                                    >
                                                        <option value="">Select Option...</option>
                                                        {attributeValues.map(v => (
                                                            <option key={v.id} value={v.id}>{v.name}</option>
                                                        ))}
                                                    </select>
                                                </div>
                                                <div>
                                                    <label className="block text-xs font-semibold text-gray-500 mb-1">Price Override</label>
                                                    <input
                                                        type="number"
                                                        value={variantForm.price}
                                                        onChange={e => setVariantForm({ ...variantForm, price: Number(e.target.value) })}
                                                        className="w-full rounded-lg border border-gray-300 dark:border-gray-700 px-3 py-2 text-sm dark:bg-gray-900 dark:text-white"
                                                        placeholder="0.00"
                                                    />
                                                </div>
                                                <div>
                                                    <label className="block text-xs font-semibold text-gray-500 mb-1">Quantity</label>
                                                    <input
                                                        type="number"
                                                        value={variantForm.qty}
                                                        onChange={e => setVariantForm({ ...variantForm, qty: Number(e.target.value) })}
                                                        className="w-full rounded-lg border border-gray-300 dark:border-gray-700 px-3 py-2 text-sm dark:bg-gray-900 dark:text-white"
                                                        placeholder="0"
                                                    />
                                                </div>
                                            </div>
                                            <div className="mt-4 flex justify-end">
                                                <button
                                                    type="button"
                                                    onClick={handleCreateVariant}
                                                    disabled={!variantForm.attribute_id || variantForm.value_ids.length === 0}
                                                    className="bg-brand-500 text-white px-4 py-2 rounded-lg text-sm font-bold shadow-sm hover:bg-brand-600 disabled:opacity-50"
                                                >
                                                    Add Variant
                                                </button>
                                            </div>
                                        </div>

                                        {/* Existing Variants List */}
                                        <div className="rounded-xl border border-gray-200 dark:border-gray-800 overflow-hidden">
                                            <table className="w-full text-sm text-left">
                                                <thead className="bg-gray-50 dark:bg-gray-800 text-gray-500 uppercase text-xs">
                                                    <tr>
                                                        <th className="px-6 py-3">Variant</th>
                                                        <th className="px-6 py-3">Price</th>
                                                        <th className="px-6 py-3">Qty</th>
                                                        <th className="px-6 py-3 text-right">Actions</th>
                                                    </tr>
                                                </thead>
                                                <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                                                    {productVariants.length === 0 ? (
                                                        <tr>
                                                            <td colSpan={4} className="px-6 py-8 text-center text-gray-500">
                                                                No variants added yet.
                                                            </td>
                                                        </tr>
                                                    ) : (
                                                        productVariants.map((variant: any) => (
                                                            <tr key={variant.id} className="bg-white dark:bg-gray-900">
                                                                <td className="px-6 py-4 font-medium text-gray-900 dark:text-white">
                                                                    {variant.attribute_values.map((av: any) => (
                                                                        <span key={av.id} className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-200 mr-2">
                                                                            {av.attribute?.name}: {av.name}
                                                                        </span>
                                                                    ))}
                                                                </td>
                                                                <td className="px-6 py-4 font-mono">${variant.price}</td>
                                                                <td className="px-6 py-4">{variant.qty}</td>
                                                                <td className="px-6 py-4 text-right">
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => handleDeleteVariant(variant.id)}
                                                                        className="text-red-500 hover:text-red-700 font-medium text-xs"
                                                                    >
                                                                        Remove
                                                                    </button>
                                                                </td>
                                                            </tr>
                                                        ))
                                                    )}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                )}
                </form>
            </CatalogModalFrame>

            <MediaPickerModal
                isOpen={isMediaModalOpen}
                onClose={() => setIsMediaModalOpen(false)}
                title="Select Product Images"
                multiple
                selectedIds={form.images}
                onSelectMultipleIds={(ids) => {
                    setForm((prev) => ({
                        ...prev,
                        images: Array.from(new Set(ids))
                    }));
                    setIsMediaModalOpen(false);
                }}
            />

            <div className="rounded-xl border border-gray-200 bg-white dark:border-gray-800 dark:bg-gray-900 overflow-hidden shadow-sm">
                <div className="p-4 border-b border-gray-200 dark:border-gray-800 flex justify-between items-center bg-gray-50/50 dark:bg-gray-800/50">
                    <h3 className="text-lg font-semibold text-gray-800 dark:text-white">Existing Products</h3>
                    <div className="flex items-center gap-3">
                        <div className="flex items-center bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-0.5 shadow-sm">
                            <button
                                onClick={() => setViewMode('list')}
                                className={`p-1.5 rounded-md transition-all ${viewMode === 'list' ? 'bg-brand-500 text-white shadow-sm' : 'text-gray-400 hover:text-gray-600 dark:hover:text-gray-300'}`}
                                title="List View"
                            >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" /></svg>
                            </button>
                            <button
                                onClick={() => setViewMode('grid')}
                                className={`p-1.5 rounded-md transition-all ${viewMode === 'grid' ? 'bg-brand-500 text-white shadow-sm' : 'text-gray-400 hover:text-gray-600 dark:hover:text-gray-300'}`}
                                title="Grid View"
                            >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1H5a1 1 0 01-1-1V5zm10 0a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1V5zM4 15a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1H5a1 1 0 01-1-1v-4zm10 0a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1v-4z" /></svg>
                            </button>
                            <button
                                onClick={() => setViewMode('table')}
                                className={`p-1.5 rounded-md transition-all ${viewMode === 'table' ? 'bg-brand-500 text-white shadow-sm' : 'text-gray-400 hover:text-gray-600 dark:hover:text-gray-300'}`}
                                title="Table View"
                            >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M3 14h18M3 6h18M3 18h18M10 6v12M17 6v12" /></svg>
                            </button>
                        </div>
                        {loading && <div className="animate-spin rounded-full h-4 w-4 border-2 border-brand-500 border-t-transparent"></div>}
                    </div>
                </div>

                {products.length === 0 ? (
                    <div className="p-12 text-center">
                        <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-gray-500 mb-3">
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" /></svg>
                        </div>
                        <p className="text-gray-500 dark:text-gray-400 font-medium">No products found matching your filters.</p>
                    </div>
                ) : (
                    <>
                        {/* ═══ LIST VIEW ═══ */}
                        {viewMode === 'list' && (
                            <div className="divide-y divide-gray-100 dark:divide-gray-800">
                                {products.map((p) => {
                                    const brand = resolveProductBrand(p);
                                    const category = resolveProductCategory(p);
                                    const brandName = brand?.name || 'No Brand';
                                    const categoryName = category?.name || 'N/A';
                                    const productImage = getProductListingImage(p);
                                    const brandImage = getImageSource(brand?.images);
                                    const categoryImage = getImageSource(category?.files) || getImageSource(category?.images?.[0] || null);

                                    return (
                                    <div key={p.id} className={`p-5 transition-all hover:bg-gray-50 dark:hover:bg-gray-800/40 relative group ${editingId === p.id ? 'bg-brand-50/50 dark:bg-brand-900/10 ring-1 ring-inset ring-brand-200 dark:ring-brand-900/30' : ''}`}>
                                        <div className="flex flex-col gap-4">
                                            <div className="flex items-start justify-between gap-4">
                                                <div className="min-w-0 flex items-start gap-3">
                                                    <div className="w-14 h-14 rounded-xl overflow-hidden border border-gray-200 dark:border-gray-700 bg-gray-100 dark:bg-gray-800 shrink-0">
                                                        {productImage ? (
                                                            <img
                                                                src={productImage}
                                                                alt={p.name}
                                                                className="w-full h-full object-cover"
                                                            />
                                                        ) : (
                                                            <div className="w-full h-full flex items-center justify-center text-xs font-bold text-gray-500 dark:text-gray-300">
                                                                {getInitials(p.name)}
                                                            </div>
                                                        )}
                                                    </div>
                                                    <div className="min-w-0">
                                                        <h4 className="text-base font-bold text-gray-900 dark:text-white truncate group-hover:text-brand-600 dark:group-hover:text-brand-400 transition-colors">
                                                            {p.name}
                                                        </h4>
                                                        <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 flex items-center gap-1.5 font-medium">
                                                            {brandImage ? (
                                                                <img
                                                                    src={brandImage}
                                                                    alt={brandName}
                                                                    className="w-4 h-4 rounded object-cover border border-gray-200 dark:border-gray-700"
                                                                />
                                                            ) : (
                                                                <div className="w-4 h-4 rounded bg-gray-100 dark:bg-gray-800 flex items-center justify-center text-[8px] font-bold text-gray-500 dark:text-gray-300 border border-gray-200 dark:border-gray-700">
                                                                    {getInitials(brandName, 'NB')}
                                                                </div>
                                                            )}
                                                            {brandName}
                                                        </div>
                                                    </div>
                                                </div>
                                                <span className={`shrink-0 inline-flex items-center px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider shadow-sm ${p.active
                                                    ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 border border-green-200/50 dark:border-green-900/50'
                                                    : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400 border border-red-200/50 dark:border-red-900/50'
                                                    }`}>
                                                    {p.active ? 'Active' : 'Inactive'}
                                                </span>
                                            </div>
                                            <div className="flex flex-wrap gap-2">
                                                <div className="flex items-center gap-1.5 px-2 py-1 rounded-lg bg-white dark:bg-gray-800 text-xs text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-700 shadow-sm">
                                                    <span className="font-bold text-gray-400 dark:text-gray-500 uppercase text-[9px]">SKU</span>
                                                    <span className="font-mono">{p.sku}</span>
                                                </div>
                                                <div className="flex items-center gap-1.5 px-2 py-1 rounded-lg bg-white dark:bg-gray-800 text-xs text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-700 shadow-sm">
                                                    <span className="font-bold text-gray-400 dark:text-gray-500 uppercase text-[9px]">Cat</span>
                                                    {categoryImage ? (
                                                        <img
                                                            src={categoryImage}
                                                            alt={categoryName}
                                                            className="w-3.5 h-3.5 rounded object-cover border border-gray-200 dark:border-gray-700"
                                                        />
                                                    ) : (
                                                        <div className="w-3.5 h-3.5 rounded bg-gray-100 dark:bg-gray-700 flex items-center justify-center text-[7px] font-bold text-gray-500 dark:text-gray-300 border border-gray-200 dark:border-gray-600">
                                                            {getInitials(categoryName)}
                                                        </div>
                                                    )}
                                                    {categoryName}
                                                </div>
                                                <div className="flex items-center gap-1.5 px-2 py-1 rounded-lg bg-blue-50/50 dark:bg-blue-900/20 text-xs text-blue-600 dark:text-blue-400 border border-blue-100 dark:border-blue-900/30 shadow-sm">
                                                    <span className="font-bold text-blue-400 dark:text-blue-500 uppercase text-[9px]">Type</span>
                                                    <span className="capitalize">{p.product_type}</span>
                                                </div>
                                            </div>
                                            <div className="flex items-center justify-between gap-4 pt-3 border-t border-gray-100 dark:border-gray-800/50">
                                                <div className="flex flex-col">
                                                    <span className="text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-tight">Price</span>
                                                    <span className="text-xl font-black text-gray-900 dark:text-white leading-none">${p.price}</span>
                                                </div>
                                                <div className="flex gap-2">
                                                    <button onClick={() => handleEdit(p)} className="p-2 rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-500 hover:text-brand-500 hover:border-brand-200 shadow-sm transition-all active:scale-95" title="Edit Product">
                                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                                                    </button>
                                                    <button
                                                        onClick={() => handleDeleteProduct(p.id)}
                                                        className="p-2 rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-500 hover:text-red-500 hover:border-red-200 shadow-sm transition-all active:scale-95" title="Delete Product"
                                                    >
                                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                );
                                })}
                            </div>
                        )}

                        {/* ═══ GRID VIEW ═══ */}
                        {viewMode === 'grid' && (
                            <div className="p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                                {products.map((p) => {
                                    const brand = resolveProductBrand(p);
                                    const category = resolveProductCategory(p);
                                    const brandName = brand?.name || 'No Brand';
                                    const categoryName = category?.name || 'N/A';
                                    const productImage = getProductListingImage(p);
                                    const brandImage = getImageSource(brand?.images);
                                    const categoryImage = getImageSource(category?.files) || getImageSource(category?.images?.[0] || null);

                                    return (
                                    <div key={p.id} className={`group rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800/60 p-4 flex flex-col gap-3 transition-all hover:shadow-md hover:border-brand-200 dark:hover:border-brand-800 ${editingId === p.id ? 'ring-2 ring-brand-500/30' : ''}`}>
                                        <div className="aspect-[4/3] rounded-xl overflow-hidden border border-gray-100 dark:border-gray-700 bg-gray-100 dark:bg-gray-800">
                                            {productImage ? (
                                                <img
                                                    src={productImage}
                                                    alt={p.name}
                                                    className="w-full h-full object-cover"
                                                />
                                            ) : (
                                                <div className="w-full h-full flex items-center justify-center text-sm font-bold text-gray-500 dark:text-gray-300">
                                                    {getInitials(p.name)}
                                                </div>
                                            )}
                                        </div>
                                        <div className="flex items-start justify-between gap-2">
                                            <div className="min-w-0 flex-1">
                                                <h4 className="text-sm font-bold text-gray-900 dark:text-white truncate group-hover:text-brand-600 dark:group-hover:text-brand-400 transition-colors">
                                                    {p.name}
                                                </h4>
                                                <div className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5 truncate font-medium flex items-center gap-1.5">
                                                    {brandImage ? (
                                                        <img
                                                            src={brandImage}
                                                            alt={brandName}
                                                            className="w-4 h-4 rounded object-cover border border-gray-200 dark:border-gray-700"
                                                        />
                                                    ) : (
                                                        <div className="w-4 h-4 rounded bg-gray-100 dark:bg-gray-700 flex items-center justify-center text-[8px] font-bold text-gray-500 dark:text-gray-300 border border-gray-200 dark:border-gray-600">
                                                            {getInitials(brandName, 'NB')}
                                                        </div>
                                                    )}
                                                    <span className="truncate">{brandName}</span>
                                                </div>
                                            </div>
                                            <span className={`shrink-0 inline-flex items-center px-2 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wider ${p.active
                                                ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                                                : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                                                }`}>
                                                {p.active ? 'Active' : 'Inactive'}
                                            </span>
                                        </div>
                                        <div className="flex flex-wrap gap-1.5">
                                            <span className="px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-700 text-[10px] text-gray-600 dark:text-gray-300 font-mono">{p.sku}</span>
                                            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-700 text-[10px] text-gray-600 dark:text-gray-300 font-medium">
                                                {categoryImage ? (
                                                    <img
                                                        src={categoryImage}
                                                        alt={categoryName}
                                                        className="w-3 h-3 rounded object-cover border border-gray-200 dark:border-gray-600"
                                                    />
                                                ) : (
                                                    <div className="w-3 h-3 rounded bg-gray-200 dark:bg-gray-600 flex items-center justify-center text-[7px] font-bold text-gray-600 dark:text-gray-200">
                                                        {getInitials(categoryName)}
                                                    </div>
                                                )}
                                                <span className="max-w-[90px] truncate">{categoryName}</span>
                                            </span>
                                            <span className="px-1.5 py-0.5 rounded bg-blue-50 dark:bg-blue-900/20 text-[10px] text-blue-600 dark:text-blue-400 capitalize font-medium">{p.product_type}</span>
                                        </div>
                                        <div className="flex items-center justify-between mt-auto pt-3 border-t border-gray-100 dark:border-gray-700/50">
                                            <span className="text-lg font-black text-gray-900 dark:text-white">${p.price}</span>
                                            <div className="flex gap-1.5">
                                                <button onClick={() => handleEdit(p)} className="p-1.5 rounded-lg text-gray-400 hover:text-brand-500 hover:bg-brand-50 dark:hover:bg-brand-900/20 transition-all" title="Edit">
                                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                                                </button>
                                                <button
                                                    onClick={() => handleDeleteProduct(p.id)}
                                                    className="p-1.5 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-all" title="Delete"
                                                >
                                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                );
                                })}
                            </div>
                        )}

                        {/* ═══ TABLE VIEW ═══ */}
                        {viewMode === 'table' && (
                            <div className="overflow-x-auto">
                                <table className="w-full text-sm text-left">
                                    <thead className="bg-gray-50 dark:bg-gray-800/80 text-gray-500 dark:text-gray-400 uppercase text-xs border-b border-gray-200 dark:border-gray-700">
                                        <tr>
                                            <th className="px-5 py-3 font-semibold tracking-wider">Product</th>
                                            <th className="px-5 py-3 font-semibold tracking-wider">SKU</th>
                                            <th className="px-5 py-3 font-semibold tracking-wider">Category</th>
                                            <th className="px-5 py-3 font-semibold tracking-wider">Type</th>
                                            <th className="px-5 py-3 font-semibold tracking-wider">Price</th>
                                            <th className="px-5 py-3 font-semibold tracking-wider">Status</th>
                                            <th className="px-5 py-3 font-semibold tracking-wider text-right">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                                        {products.map((p) => {
                                            const brand = resolveProductBrand(p);
                                            const category = resolveProductCategory(p);
                                            const brandName = brand?.name || 'No Brand';
                                            const categoryName = category?.name || 'N/A';
                                            const productImage = getProductListingImage(p);
                                            const brandImage = getImageSource(brand?.images);
                                            const categoryImage = getImageSource(category?.files) || getImageSource(category?.images?.[0] || null);

                                            return (
                                            <tr key={p.id} className={`transition-colors hover:bg-gray-50 dark:hover:bg-gray-800/40 ${editingId === p.id ? 'bg-brand-50/50 dark:bg-brand-900/10' : 'bg-white dark:bg-gray-900'}`}>
                                                <td className="px-5 py-3.5">
                                                    <div className="min-w-0 flex items-center gap-3">
                                                        <div className="w-12 h-12 rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700 bg-gray-100 dark:bg-gray-800 shrink-0">
                                                            {productImage ? (
                                                                <img
                                                                    src={productImage}
                                                                    alt={p.name}
                                                                    className="w-full h-full object-cover"
                                                                />
                                                            ) : (
                                                                <div className="w-full h-full flex items-center justify-center text-[10px] font-bold text-gray-500 dark:text-gray-300">
                                                                    {getInitials(p.name)}
                                                                </div>
                                                            )}
                                                        </div>
                                                        <div className="min-w-0">
                                                            <p className="font-semibold text-gray-900 dark:text-white truncate max-w-[200px]">{p.name}</p>
                                                            <div className="text-xs text-gray-500 dark:text-gray-400 truncate flex items-center gap-1.5">
                                                                {brandImage ? (
                                                                    <img
                                                                        src={brandImage}
                                                                        alt={brandName}
                                                                        className="w-4 h-4 rounded object-cover border border-gray-200 dark:border-gray-700"
                                                                    />
                                                                ) : (
                                                                    <div className="w-4 h-4 rounded bg-gray-100 dark:bg-gray-700 flex items-center justify-center text-[8px] font-bold text-gray-500 dark:text-gray-300 border border-gray-200 dark:border-gray-600">
                                                                        {getInitials(brandName, 'NB')}
                                                                    </div>
                                                                )}
                                                                <span className="truncate">{brandName}</span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="px-5 py-3.5 font-mono text-xs text-gray-600 dark:text-gray-300">{p.sku}</td>
                                                <td className="px-5 py-3.5 text-xs text-gray-600 dark:text-gray-300">
                                                    <div className="flex items-center gap-1.5">
                                                        {categoryImage ? (
                                                            <img
                                                                src={categoryImage}
                                                                alt={categoryName}
                                                                className="w-4 h-4 rounded object-cover border border-gray-200 dark:border-gray-700"
                                                            />
                                                        ) : (
                                                            <div className="w-4 h-4 rounded bg-gray-100 dark:bg-gray-700 flex items-center justify-center text-[8px] font-bold text-gray-500 dark:text-gray-300 border border-gray-200 dark:border-gray-600">
                                                                {getInitials(categoryName)}
                                                            </div>
                                                        )}
                                                        <span>{categoryName}</span>
                                                    </div>
                                                </td>
                                                <td className="px-5 py-3.5">
                                                    <span className="px-2 py-0.5 rounded-full bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 text-xs font-medium capitalize">{p.product_type}</span>
                                                </td>
                                                <td className="px-5 py-3.5 font-mono font-bold text-gray-900 dark:text-white">${p.price}</td>
                                                <td className="px-5 py-3.5">
                                                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${p.active
                                                        ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                                                        : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                                                        }`}>
                                                        {p.active ? 'Active' : 'Inactive'}
                                                    </span>
                                                </td>
                                                <td className="px-5 py-3.5">
                                                    <div className="flex gap-1.5 justify-end">
                                                        <button onClick={() => handleEdit(p)} className="p-1.5 rounded-lg text-gray-400 hover:text-brand-500 hover:bg-brand-50 dark:hover:bg-brand-900/20 transition-all" title="Edit">
                                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                                                        </button>
                                                        <button
                                                            onClick={() => handleDeleteProduct(p.id)}
                                                            className="p-1.5 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-all" title="Delete"
                                                        >
                                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </>
                )}
            </div>

            {!loading && totalItems > 0 ? (
                <div className="rounded-xl border border-gray-200 bg-white dark:border-gray-800 dark:bg-gray-900 overflow-hidden shadow-sm">
                    <Pagination
                        currentPage={pagination?.currentPage ?? currentPage}
                        totalPages={totalPages}
                        onPageChange={setCurrentPage}
                        rowsPerPage={pagination?.perPage ?? rowsPerPage}
                        onRowsPerPageChange={(rows) => {
                            setRowsPerPage(rows);
                            setCurrentPage(1);
                        }}
                        totalItems={totalItems}
                    />
                </div>
            ) : null}
        </div>
    );
}
